import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ChatIconOutline, ChatMessageDisplay, CheckMarkIcon, CloseIcon, LeftCaretIcon, PlusIconPlain, SpeakerItem, UpArrowIcon, } from '@streamr/tv-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { UserService } from '../../services/user/UserService';
import { AcceptedParticipantType } from '../../services/user/UserServiceOutput';
import { PeopleIcon } from './PeopleIcon';
export function IncomingRequestsButton({ variant = 'desktop', popupPlacement = 'above', }) {
    const incomingRequests = UserService.getOutput().useIncomingRequests();
    const displayNames = UserService.getOutput().useDisplayNames();
    const acceptedParticipantType = UserService.getOutput().useAcceptedParticipantType();
    const [popupOpen, setPopupOpen] = useState(false);
    const [chatPerson, setChatPerson] = useState(null);
    const buttonRef = useRef(null);
    const popupRef = useRef(null);
    const closeTimeoutRef = useRef();
    const [popupPos, setPopupPos] = useState(null);
    const updatePopupPosition = useCallback(() => {
        if (!buttonRef.current || popupPlacement === 'below')
            return;
        const rect = buttonRef.current.getBoundingClientRect();
        setPopupPos({
            top: rect.top,
            left: rect.left + rect.width / 2,
        });
    }, [popupPlacement]);
    useEffect(() => {
        if (popupOpen) {
            updatePopupPosition();
        }
    }, [popupOpen, updatePopupPosition]);
    useEffect(() => {
        if (!popupOpen) {
            setChatPerson(null);
        }
    }, [popupOpen]);
    const isCallMode = acceptedParticipantType === AcceptedParticipantType.SPEAKERS;
    if (incomingRequests.length === 0) {
        return null;
    }
    const resolveName = (userId) => displayNames[userId] || userId;
    const permissionService = UserService.getNotificationInterface();
    const scheduleClose = () => {
        closeTimeoutRef.current = setTimeout(() => {
            setPopupOpen(false);
        }, 200);
    };
    const cancelClose = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = undefined;
        }
    };
    const handleButtonMouseEnter = () => {
        cancelClose();
        setPopupOpen(true);
    };
    const handleButtonMouseLeave = () => {
        if (!chatPerson) {
            scheduleClose();
        }
    };
    const handlePopupMouseEnter = () => {
        cancelClose();
    };
    const handlePopupMouseLeave = () => {
        if (!chatPerson) {
            scheduleClose();
        }
    };
    const count = incomingRequests.length;
    const label = count === 1 ? '1 Request' : `${count} Requests`;
    const popupTitle = acceptedParticipantType === AcceptedParticipantType.GUESTS
        ? `${count} user${count !== 1 ? 's are' : ' is'} requesting to be a guest`
        : `${count} user${count !== 1 ? 's are' : ' is'} requesting to join`;
    const popupContent = (_jsx(PopupRoot, { ref: popupRef, "$placement": popupPlacement, style: popupPlacement === 'above' && popupPos
            ? {
                top: `${popupPos.top}px`,
                left: `${popupPos.left}px`,
            }
            : undefined, ...(variant === 'desktop'
            ? {
                onMouseEnter: handlePopupMouseEnter,
                onMouseLeave: handlePopupMouseLeave,
            }
            : {}), children: !isCallMode && chatPerson ? (_jsx(InlineChat, { person: chatPerson, onBack: () => setChatPerson(null) })) : (_jsxs(_Fragment, { children: [_jsx(PopupHeader, { children: popupTitle }), _jsx(PopupList, { children: incomingRequests.map((request) => (_jsx(SpeakerItem, { name: resolveName(request.userId), actions: _jsxs(_Fragment, { children: [_jsx(AcceptButton, { kind: "pill", className: "tv-ui-shiny-border", onClick: () => {
                                        permissionService.notifyAcceptPermission(request.userId, request.userRole);
                                    }, children: _jsx(CheckMarkIcon, {}) }), _jsx(DenyButton, { kind: "pill", onClick: () => {
                                        permissionService.notifyRejectPermission(request.userId, request.userRole);
                                    }, children: _jsx(CloseIcon, {}) }), !isCallMode && (_jsx(ChatButton, { kind: "pill", onClick: () => {
                                        setChatPerson({
                                            id: request.userId,
                                            name: resolveName(request.userId),
                                        });
                                    }, children: _jsx(ChatIconOutline, {}) }))] }) }, request.userId))) })] })) }));
    const renderPopup = () => {
        if (popupPlacement === 'below') {
            return popupContent;
        }
        if (!popupPos)
            return null;
        return createPortal(popupContent, document.body);
    };
    return (_jsxs(Container, { ref: buttonRef, ...(variant === 'desktop'
            ? {
                onMouseEnter: handleButtonMouseEnter,
                onMouseLeave: handleButtonMouseLeave,
            }
            : {}), children: [_jsx(ButtonRoot, { type: "button", "$variant": variant, onClick: () => setPopupOpen((v) => !v), children: variant === 'mobile' ? (_jsxs(_Fragment, { children: [_jsx(StyledPeopleIcon, { width: 20, height: 20 }), _jsx(MobileBadge, { children: count })] })) : (_jsxs(_Fragment, { children: [_jsx(StyledPeopleIcon, { width: 18, height: 18 }), _jsx("span", { children: label }), _jsx(StyledPlusIcon, {})] })) }), popupOpen && renderPopup()] }));
}
function InlineChat({ person, onBack, }) {
    const messages = UserService.getOutput().usePrivateChatMessagesFromUser(person.id);
    const permissionService = UserService.getNotificationInterface();
    const inputRef = useRef(null);
    const sendMessage = () => {
        if (!inputRef.current)
            return;
        const text = inputRef.current.value.trim();
        if (text) {
            permissionService.notifySendPrivateMessage(person.id, text);
            inputRef.current.value = '';
        }
    };
    return (_jsxs(ChatContainer, { children: [_jsxs(ChatHeader, { children: [_jsx(BackButton, { kind: "pill", onClick: onBack, children: _jsx(LeftCaretIcon, {}) }), _jsx(ChatPersonName, { children: person.name })] }), _jsx(ChatMessages, { children: messages.length === 0 ? (_jsx(EmptyChat, { children: "No messages yet" })) : (messages.map((msg, i) => (_jsx(ChatMessageDisplay
                // biome-ignore lint/suspicious/noArrayIndexKey: no stable id on PrivateChatMessage; timestamp may collide
                , { content: msg.message, authorNickname: msg.isSent ? 'You' : person.name }, `${msg.timestamp}-${i}`)))) }), _jsxs(ChatInputRow, { children: [_jsx(ChatInput, { ref: inputRef, placeholder: "Send a message...", onKeyDown: (e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                sendMessage();
                            }
                        } }), _jsx(SendButton, { kind: "pill", onClick: sendMessage, children: _jsx(UpArrowIcon, {}) })] })] }));
}
const Container = styled.div `
    position: relative;
    display: flex;
    align-items: center;
`;
const ButtonRoot = styled(Button) `
    --Button_Background: var(--tv-ui-color-9);
    --Button_HoverBackground: var(--tv-ui-color-9);
    --Button_TextColor: var(--tv-ui-color-1);
    --Button_Padding: ${({ $variant }) => $variant === 'mobile' ? '0 0.75rem' : '0 1.25rem'};
    --Button_BorderRadius: 1.5rem;
    --Button_Height: 3rem;
    position: relative;
    gap: 0.5rem;
    font-weight: 600;
    font-size: 0.875rem;
`;
const StyledPeopleIcon = styled(PeopleIcon) `
    flex-shrink: 0;
`;
const StyledPlusIcon = styled(PlusIconPlain) `
    width: 0.75rem;
    height: 0.75rem;
    flex-shrink: 0;
`;
const MobileBadge = styled.div `
    position: absolute;
    top: 0;
    right: 0;
    background: var(--tv-ui-color-1);
    color: var(--tv-ui-color-3);
    border-radius: 100px;
    min-width: 1rem;
    height: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.25rem;
    font-family: 'Poppins', sans-serif;
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1;
`;
const PopupRoot = styled.div `
    position: ${({ $placement }) => $placement === 'below' ? 'absolute' : 'fixed'};
    ${({ $placement }) => $placement === 'below'
    ? `
        top: calc(100% + 0.5rem);
        left: 0;
    `
    : `
        transform: translate(-50%, calc(-100% - 0.75rem));
    `}
    min-width: 18rem;
    max-width: 22rem;
    background: rgba(57, 56, 56, 0.85);
    backdrop-filter: blur(34px);
    border: 0.0625rem solid rgba(255, 255, 255, 0.15);
    border-radius: 1rem;
    padding: 1rem;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    pointer-events: auto;

    @media (max-width: 30rem) and (orientation: portrait) {
        left: 1rem !important;
        right: 1rem;
        min-width: 0;
        max-width: none;
        width: auto;
        transform: translateY(calc(-100% - 0.75rem));
    }
`;
const PopupHeader = styled.div `
    font-family: 'Poppins', sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    padding-bottom: 0.25rem;
`;
const PopupList = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;
const AcceptButton = styled(Button) `
    --Button_Background: rgba(47, 47, 47, 0.6);
    --Button_HoverBackground: rgba(80, 80, 80, 0.8);
    --Button_Width: 2rem;
    --Button_Height: 2rem;
    --tv-ui-shiny-border-thickness: 0.0625rem;
    transition: transform 0.15s ease;

    &:hover {
        transform: scale(1.1);
    }

    svg {
        transform: scale(0.85);
    }
`;
const DenyButton = styled(Button) `
    --Button_Background: rgba(255, 255, 255, 0);
    --Button_HoverBackground: rgba(255, 255, 255, 0.05);
    --Button_Width: 2rem;
    --Button_Height: 2rem;
    color: rgba(255, 255, 255, 0.6);

    &:hover {
        color: rgba(255, 255, 255, 0.9);
    }

    svg {
        transform: scale(0.85);
    }
`;
const ChatButton = styled(Button) `
    --Button_Background: rgba(255, 255, 255, 0);
    --Button_HoverBackground: rgba(255, 255, 255, 0.05);
    --Button_Width: 2rem;
    --Button_Height: 2rem;
    color: rgba(255, 255, 255, 0.6);

    &:hover {
        color: rgba(255, 255, 255, 0.9);
    }

    svg {
        transform: scale(0.85);
    }
`;
const ChatContainer = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;
const ChatHeader = styled.div `
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;
const BackButton = styled(Button) `
    --Button_Height: 1.75rem;
    --Button_Width: 1.75rem;
    color: rgba(255, 255, 255, 0.8);

    svg {
        transform: scale(0.75);
    }
`;
const ChatPersonName = styled.div `
    font-family: 'Poppins', sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
`;
const ChatMessages = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 12rem;
    overflow-y: auto;
`;
const EmptyChat = styled.div `
    font-size: 0.8125rem;
    color: rgba(255, 255, 255, 0.4);
    text-align: center;
    padding: 1rem 0;
`;
const ChatInputRow = styled.div `
    display: flex;
    gap: 0.5rem;
    align-items: center;
`;
const ChatInput = styled.input `
    flex: 1;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0.5rem;
    padding: 0.5rem 0.75rem;
    color: white;
    font-size: 0.8125rem;
    font-family: inherit;

    &::placeholder {
        color: rgba(255, 255, 255, 0.4);
    }

    &:focus {
        outline: none;
        border-color: rgba(255, 255, 255, 0.25);
    }
`;
const SendButton = styled(Button) `
    --Button_Width: 2rem;
    --Button_Height: 2rem;
    --Button_TextColor: black;
    --Button_Background: white;
    --Button_HoverBackground: rgba(255, 255, 255, 0.85);

    svg {
        transform: scale(0.85);
    }
`;
